import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your account email.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/reset-password`,
      });

      if (resetError) throw resetError;

      setSuccess('Reset link sent. Check your email to update your password.');
    } catch (err: any) {
      setError(err.message || 'Unable to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fafc]">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <Logo size={100} className="mx-auto mb-6 drop-shadow-2xl" />
          <h1 className="text-3xl font-black text-[#0f172a] tracking-tight">Forgot Password</h1>
          <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em] mt-3">Recover your account by email</p>
        </div>

        <div className="bg-white p-10 rounded-[3.5rem] shadow-[0_40px_80px_-15px_rgba(0,0,0,0.06)] border border-slate-50">
          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Account Email</label>
              <div className="relative group">
                <Mail className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f172a] transition-colors w-5 h-5" />
                <input
                  type="email"
                  required
                  className="w-full pl-16 pr-6 py-5 bg-slate-50 border border-transparent rounded-[2rem] focus:bg-white focus:border-slate-200 focus:ring-4 focus:ring-slate-50 outline-none text-slate-900 font-bold transition-all"
                  placeholder="Enter your registered email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {success && (
              <div className="p-5 rounded-[2rem] bg-emerald-50 border border-emerald-100 flex items-center gap-4">
                <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                <p className="text-emerald-700 text-[9px] font-black uppercase tracking-wider leading-tight">{success}</p>
              </div>
            )}

            {error && (
              <div className="p-5 rounded-[2rem] bg-red-50 border border-red-100 flex items-center gap-4">
                <AlertCircle size={20} className="text-red-500 shrink-0" />
                <p className="text-red-600 text-[9px] font-black uppercase tracking-wider leading-tight">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0f172a] text-amber-400 py-6 rounded-[2.5rem] font-black shadow-2xl flex items-center justify-center hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 border border-white/5"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <><Send className="mr-3 w-5 h-5" /> Send Reset Link</>
              )}
            </button>
          </form>
        </div>

        <button
          onClick={() => navigate('/login')}
          className="mt-8 mx-auto flex items-center text-slate-400 hover:text-slate-600 font-black text-[10px] uppercase tracking-widest transition-colors"
        >
          <ArrowLeft size={14} className="mr-2" /> Back to Login
        </button>
      </div>
    </div>
  );
};

export default ForgotPassword;
